import type { ReactNode } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Flag,
  Inbox,
  FileText,
  Image,
  Smartphone,
  LogOut,
  Moon,
  Sun,
} from "lucide-react"
import { useAuth } from "../lib/auth-context"
import { useTheme } from "../lib/theme-provider"
import { cn } from "../lib/cn"

const nav = [
  { to: "/", label: "الرئيسية", icon: LayoutDashboard },
  { to: "/users", label: "المستخدمون", icon: Users },
  { to: "/services", label: "الخدمات", icon: Briefcase },
  { to: "/reports", label: "التقارير", icon: Flag },
  { to: "/support", label: "الرسائل", icon: Inbox },
  { to: "/content", label: "إدارة المحتوى", icon: FileText },
  { to: "/ad-banner", label: "شريط الإعلانات", icon: Image },
  { to: "/app-config", label: "إعدادات التطبيق", icon: Smartphone },
]

export function AdminLayout({ children }: { children: ReactNode }) {
  const { signOut, user } = useAuth()
  const { theme, toggle } = useTheme()
  const location = useLocation()

  return (
    <div className="flex min-h-screen bg-surface-muted">
      <aside className="flex w-64 shrink-0 flex-col border-e border-border-subtle bg-surface p-lg">
        <div className="mb-2xl flex items-center gap-sm px-2xs">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo-mark-trim.png" alt="عزبتنا" className="h-9 w-auto" />
          <p className="text-caption text-text-secondary">لوحة الإدارة</p>
        </div>

        <nav className="flex flex-1 flex-col gap-2xs overflow-y-auto">
          {nav.map((item) => {
            const isActive = location.pathname === item.to
            const Icon = item.icon
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-sm rounded-button px-md py-sm text-body-sm font-semibold transition-colors",
                  isActive
                    ? "bg-accent/10 text-accent"
                    : "text-text-secondary hover:bg-surface-muted hover:text-text-primary",
                )}
              >
                <Icon className={cn("size-4.5 shrink-0", isActive ? "text-accent" : "text-text-secondary")} />
                {item.label}
              </Link>
            )
          })}
        </nav>

        <div className="mt-lg flex flex-col gap-sm border-t border-border-subtle pt-lg">
          <div className="flex items-center justify-between gap-sm px-2xs">
            <p className="truncate text-caption text-text-secondary" title={user?.email ?? undefined}>
              {user?.email}
            </p>
            <button
              type="button"
              onClick={toggle}
              aria-label={theme === "dark" ? "التبديل إلى الوضع الفاتح" : "التبديل إلى الوضع الداكن"}
              title={theme === "dark" ? "التبديل إلى الوضع الفاتح" : "التبديل إلى الوضع الداكن"}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-button text-text-secondary transition-colors hover:bg-surface-muted hover:text-text-primary"
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
          </div>

          <button
            type="button"
            onClick={signOut}
            className="flex items-center justify-center gap-xs rounded-button border border-danger/30 bg-danger/5 px-md py-sm text-body-sm font-semibold text-danger transition-colors hover:bg-danger/10"
          >
            <LogOut className="size-4" />
            تسجيل الخروج
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-2xl">
        <div className="mx-auto max-w-6xl">{children}</div>
      </main>
    </div>
  )
}
